import { Component, computed, model, ModelSignal, output, OutputEmitterRef, Signal, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatTab, MatTabGroup, MatTabLabel } from '@angular/material/tabs';
import { ButtonComponent } from '@libs/common/components';
import { IconComponent } from '@libs/components/icons';
import { NumberComponent, TextareaComponent } from '@libs/form/components';
import { AutocompleteInputModel, TextareaInputModel, TextInputModel } from '@libs/form/models';
import { QuickOrderAddTab } from '@libs/shop/models';
import { debounceTime, filter } from 'rxjs';
import { SearchSuggestionAutocompleteComponent } from '../../elements/search-suggestion-autocomplete';
import { CollectionInputData, ProductQuantityInputData, QuickOrderAddTabs, SearchInputData } from './quick-order-add.model';

@Component({
  selector: 'ui-quick-order-add',
  templateUrl: './quick-order-add.component.html',
  styleUrls: ['./quick-order-add.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ReactiveFormsModule,
    FormsModule,
    NumberComponent,
    TextareaComponent,
    SearchSuggestionAutocompleteComponent,
    MatTab,
    MatTabGroup,
    MatTabLabel,
    IconComponent,
    ButtonComponent,
  ],
})

export class QuickOrderAddComponent {
  public productsAdded: OutputEmitterRef<Record<string, number>> = output();

  public readonly activeTab: ModelSignal<QuickOrderAddTab> = model<QuickOrderAddTab>('quickorder');

  public readonly selectedTabIndex: Signal<number> = computed(() => QuickOrderAddTabs.indexOf(this.activeTab()));

  public searchControl: FormControl<string> = new FormControl<string>('', { nonNullable: true });
  public collectionSearchControl: FormControl<string> = new FormControl<string>('', { nonNullable: true });

  public quantityControl: FormControl<number> = new FormControl<number>(1, { nonNullable: true });

  public readonly searchInputConfig: AutocompleteInputModel = SearchInputData;

  public readonly searchInputConfigMultiple: TextareaInputModel = CollectionInputData;

  public readonly productQuantityInputConfig: TextInputModel = ProductQuantityInputData;

  public constructor() {
    this.searchControl.valueChanges.pipe(
      takeUntilDestroyed(),
      debounceTime(300),
      filter(() => this.searchControl.valid),
    ).subscribe(() => {
      this.quantityControl.setValue(1);
    });
  }

  public selectTab(index: number): void {
    this.activeTab.set(QuickOrderAddTabs[index]);
  }

  public addToQuickOrderList(): void {
    if (this.activeTab() === 'quickorder' && this.collectionSearchControl.value) {
      this.productsAdded.emit(this.parseProductIdentifiers(this.collectionSearchControl.value));
      this.collectionSearchControl.reset();

      return;
    }

    if (this.activeTab() === 'search' && this.searchControl.value && this.quantityControl.value) {
      const returnValue: Record<string, number> = {};

      returnValue[this.searchControl.value] = this.quantityControl.value;
      this.productsAdded.emit(returnValue);
    }
  }

  public parseProductIdentifiers(value: string): Record<string, number> {
    if (!value || value.trim() === '') {
      return {};
    }

    // Equalize all supported separators (semicolon, comma and whitespace) into line breaks
    // so that each line can be processed uniformly.
    const allValues: string[] = value
      .replace(/[;, ]/g, '\n')
      .split('\n')
      .map((line: string) => line.trim())
      .filter((line: string) => line.length > 0);

    const counts: Record<string, number> = {};

    // Index-based iteration is required to inspect adjacent values and
    // determine whether a number is an article number or a quantity.
    // e.g. 604289, 632382, 2
    for (let i: number = 0; i < allValues.length; i++) {
      const articleNumber: string = allValues[i];

      // Amounts are expected to have up to 3 digits. Everything else is treated as an articleNumber
      const isAmount: boolean = /^\d{1,3}$/.test(articleNumber);

      if (
        isAmount &&
        i > 0 &&
        /^\d{4,}$/.test(allValues[i - 1])
      ) {
        // If the current number is length wise a potential amount and not an articleNumber
        // we tend to assign it as an amount to the previous article number
        const nextNumber: string = allValues[i + 1];

        if (nextNumber && /^\d{1,3}$/.test(nextNumber)) {
          // Since two potential amounts may appear consecutively, the first
          // value is treated as a standalone articleNumber instead of an
          // amount. This allows to recognize and
          // report invalid identifiers.
          counts[articleNumber] = (counts[articleNumber] ?? 0) + 1;

        } else {
          // The happy path: the amount is used as the quantity for the previous articleNumber.
          const previousNumber: string = allValues[i - 1];

          counts[previousNumber] = Number(articleNumber);
        }
      } else {
        // Default handling:
        // - standalone articleNumber
        // - invalid identifier
        // - duplicate or double amounts
        // Standalone articles receive a quantity of 1.
        counts[articleNumber] = (counts[articleNumber] ?? 0) + 1;
      }
    }

    return counts;
  }
}
